import { docsConsoleSearchIntro, docsConsoleSearchTitle } from 'Clepit/translations';
import type { SupportedLanguage } from '@bomdisoft/translations';
import type { EditorData } from '@clepit/core';

/** Derived from `app/search-palette/index.tsx` and `app/[lang]/search/redirect.tsx`,
 * and from `app/ask-dialog/index.tsx` and `app/[lang]/ask/redirect.tsx`. Ask is
 * documented here rather than on a page of its own because
 * it is the palette's sibling: the same overlay placement, reached from the row
 * beside it. */
export const buildConsoleSearch = (language: SupportedLanguage): EditorData => ({
  blocks: [
    { data: { html: docsConsoleSearchTitle[language], level: 'h1' as const }, type: 'header' },
    { data: { html: docsConsoleSearchIntro[language] }, type: 'paragraph' },

    { data: { alt: 'The search palette over the console', caption: 'The palette: filter chips, breadcrumbed results, the match emphasised.', url: '/images/docs/console/search/screen-light.png' }, type: 'image' },
    { data: { alt: 'The search palette over the console, dark theme', caption: 'The palette: filter chips, breadcrumbed results, the match emphasised.', url: '/images/docs/console/search/screen-dark.png' }, type: 'image' },

    { data: { html: 'The palette', level: 'h2' as const }, type: 'header' },
    {
      data: {
        html: 'Two stacked cards over the dimmed canvas: the input card on top, the results card beneath. Before you type, the results card shows your recent documents; as you type, it shows breadcrumbed rows with the matching text emphasised, so you can tell two documents with the same title apart by where they live. Escape closes the palette and returns you to where you were.',
      },
      type: 'paragraph',
    },

    { data: { html: 'Filters', level: 'h2' as const }, type: 'header' },
    {
      data: {
        html: 'Four chips on the input card narrow the query: by contributor, by date, by source, and to verified documents only. Chips combine, so "verified, edited by one person, this month" is one query rather than three searches.',
      },
      type: 'paragraph',
    },

    { data: { html: 'Ways in', level: 'h2' as const }, type: 'header' },
    {
      data: {
        html: 'The Search row in the rail opens the palette in place. The /search route stays answerable for deep links and opens the palette over Home rather than rendering a separate page.',
      },
      type: 'paragraph',
    },

    { data: { html: 'Ask', level: 'h2' as const }, type: 'header' },
    {
      data: {
        html: 'Ask sits in the rail beside Search and takes the same placement: an overlay over the page you were on, never a page of its own. The composer is one growing box with the send button inside it at the end of the line, where Enter sends and Shift+Enter breaks the line. The card beneath holds the answer, and under the answer the pages it drew on, each row carrying that page icon and title; choosing a source closes the overlay and opens the page. An answer that comes back empty means nothing in the workspace matched rather than that the question failed, so it is worth rewording; Clear empties the box, and is offered only once there is something to clear. The /ask route behaves as /search does, staying answerable for deep links and opening the overlay over Home.',
      },
      type: 'paragraph',
    },
  ],
  time: 1_745_000_000_000,
  version: '1.0.0',
});
